import { Bell, MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useMyNotifications } from '@/features/notifications';
import { useMyProfile } from '@/features/profile';

/**
 * The flat gradient app-header band (handoff §06 / component sheet): the brand
 * mark + name on the inline-start, and on the inline-end a location pill (the
 * user's city) and a bell that opens the notifications inbox with a gold unread
 * dot. Reuses the `badeel-star8` motif as a faint tiled overlay. Rendered once
 * by AppShell, so it sits above every authenticated screen.
 */
export function AppHeaderBand(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const profile = useMyProfile().data;
  const notifications = useMyNotifications();
  const hasUnread = (notifications.data ?? []).some((n) => n.readAt === null);

  return (
    <header className="app-band">
      <svg className="app-band__motif" aria-hidden="true">
        <defs>
          <pattern id="app-band-star8" width="20" height="20" patternUnits="userSpaceOnUse">
            <path
              d="M10 0 L13 7 L20 10 L13 13 L10 20 L7 13 L0 10 L7 7 Z"
              fill="none"
              stroke="var(--color-primary-contrast)"
              strokeWidth="0.6"
            />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#app-band-star8)" />
      </svg>

      <div className="app-band__brand">
        <span className="app-band__mark">ب</span>
        <span className="app-band__name">{t('app.title')}</span>
      </div>

      <div className="app-band__actions">
        {profile?.city ? (
          <span className="app-band__location">
            <MapPin size={14} aria-hidden />
            {profile.city}
          </span>
        ) : null}
        <button
          type="button"
          className="app-band__bell"
          onClick={() => navigate('/notifications')}
          aria-label={t('notifications.title')}
        >
          <Bell size={20} aria-hidden />
          {hasUnread ? <span className="app-band__dot" aria-hidden /> : null}
        </button>
      </div>
    </header>
  );
}
